import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon, StatusBadge, toast, type StatusKind } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useErrorMessage } from "../../../lib/api-errors";
import { useApi } from "../../../session/session-provider";
import { useFormatters, type BillingOverview, type BillingPlan } from "../shared";
import { missingFeatures, planPrice, type BillingInterval } from "./billing-model";
import { GatedButton } from "./gated-button";
import { useInvalidateBilling } from "./use-billing";

const STATUS: Record<string, StatusKind> = { active: "success", trialing: "info", past_due: "danger", paused: "warning", canceled: "neutral" };
const FEATURE_CHUNK = 3;

interface CurrentPlanCardProps {
  overview: BillingOverview;
  plans: readonly BillingPlan[];
  canManage: boolean;
  onChangePlan: () => void;
  onSwitchAnnual: () => void;
}

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

function ScheduledChange({ overview, canManage }: { overview: BillingOverview; canManage: boolean }) {
  const { t } = useTranslation();
  const api = useApi();
  const fmt = useFormatters();
  const message = useErrorMessage();
  const invalidate = useInvalidateBilling();
  const cancel = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/billing/subscription/scheduled-change")),
    onSuccess: async () => {
      toast.success(t("billing.plan.scheduledCancelled"));
      await invalidate();
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const change = overview.scheduled_change;
  if (!change) return null;
  const plan = t(`console.plans.${change.plan_key}`, { defaultValue: change.plan_key });
  return (
    <div className="mt-4 flex items-start gap-2.5 rounded-md border border-line bg-warning-soft p-3 text-xs">
      <Icon name="calendar" className="mt-0.5 size-4 text-warning" />
      <div className="min-w-0 flex-1">
        <p className="text-fg">{t("billing.plan.scheduled", { plan, date: fmt.date(change.effective_at) })}</p>
        {Number(change.credit) > 0 ? <p className="text-fg-muted">{t("billing.plan.scheduledCredit", { amount: fmt.money(change.credit, change.currency) })}</p> : null}
      </div>
      <GatedButton allowed={canManage} variant="ghost" size="sm" loading={cancel.isPending} onClick={() => { cancel.mutate(); }}>
        {t("billing.plan.keepPlan")}
      </GatedButton>
    </div>
  );
}

export function CurrentPlanCard({ overview, plans, canManage, onChangePlan, onSwitchAnnual }: CurrentPlanCardProps) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const interval: BillingInterval = overview.interval === "annual" ? "annual" : "monthly";
  const price = planPrice(overview.plan, interval);
  const list = new Intl.ListFormat(fmt.locale, { style: "long", type: "conjunction" });
  const feature = (key: string) => t(`billing.features.${key}`, { defaultValue: key });
  const missing = missingFeatures(overview.features, plans, overview.plan.key);
  const nextPlan = plans[plans.findIndex((p) => p.key === overview.plan.key) + 1];
  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-center justify-between">
          <span className="stat-label">{t("billing.plan.current")}</span>
          <StatusBadge status={STATUS[overview.status] ?? "neutral"} label={t(`billing.status.${overview.status}`, { defaultValue: overview.status })} />
        </div>
        <h2 className="mt-2 text-xl font-extrabold">{overview.plan.name}</h2>
        <div className="mt-1 flex items-baseline gap-1">
          <span className="text-3xl font-extrabold tracking-tight tabular-nums">{price === null ? t("billing.plans.custom") : fmt.money(price, overview.currency)}</span>
          {price === null ? null : <span className="text-sm text-fg-muted">{t(interval === "annual" ? "billing.perYear" : "billing.perMonth")}</span>}
        </div>
        <p className="mt-1 text-xs text-fg-subtle">
          {t(interval === "annual" ? "billing.plan.billedAnnually" : "billing.plan.billedMonthly")} · {overview.region.tax.label}
          {overview.renews_at ? (
            <>
              {" · "}
              {t("billing.plan.renews")} <strong className="text-fg">{fmt.date(overview.renews_at)}</strong>
            </>
          ) : null}
        </p>
        <div className="divider my-4" />
        <ul className="space-y-2 text-sm">
          {chunk(overview.features, FEATURE_CHUNK).map((group) => (
            <li key={group.join()} className="flex items-center gap-2">
              <Icon name="check" className="size-4 shrink-0 text-success" />
              {list.format(group.map(feature))}
            </li>
          ))}
          {missing.length && nextPlan ? (
            <li className="flex items-center gap-2">
              <Icon name="x" className="size-4 shrink-0 text-fg-subtle" />
              <span className="text-fg-muted">{t("billing.plan.notIncluded", { features: list.format(missing.slice(0, FEATURE_CHUNK).map(feature)), plan: nextPlan.name })}</span>
            </li>
          ) : null}
        </ul>
        {overview.overage_grace_until ? (
          <p className="mt-4 flex items-start gap-2 rounded-md bg-danger-soft p-3 text-xs text-fg" role="status">
            <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0 text-danger" />
            {t("billing.plan.graceUntil", { date: fmt.dateTime(overview.overage_grace_until) })}
          </p>
        ) : null}
        <ScheduledChange overview={overview} canManage={canManage} />
        <div className="mt-5 flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={onChangePlan}>
            {t("billing.plan.change")}
          </Button>
          {interval === "monthly" && overview.plan.price_annual ? (
            <GatedButton allowed={canManage} variant="ghost" size="sm" onClick={onSwitchAnnual}>
              {t("billing.plan.switchAnnual")}
            </GatedButton>
          ) : null}
          {interval === "annual" ? <Badge>{t("billing.plan.annualActive")}</Badge> : null}
        </div>
      </div>
    </div>
  );
}
